/**
 * FallbackHandler.js
 *
 * ES6 Module providing a unified mechanism for fallback UI and behavior when
 * primary features (e.g., calendar, contact modal) fail to load or initialize.
 *
 * Responsibilities:
 * - Display user-friendly fallback messages or interfaces
 * - Log fallback activations via ErrorHandler
 * - Support custom fallback components or callbacks
 * - Expose both programmatic and declarative APIs
 *
 * Features:
 * - Graceful degradation: non-blocking UI adjustments
 * - Configurable UI container selector and message templates
 * - Named exports for flexible integration
 * - Minimal dependencies and SSR-safe operation
 *
 * Limitations:
 * - Assumes browser environment for DOM manipulation
 * - No built-in styling: relies on external CSS
 * - Basic text-only messages; no rich content support
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

class FallbackHandler {
  constructor({ containerSelector = 'body', defaultMessage = 'Content unavailable.' } = {}) {
    this.containerSelector = containerSelector;
    this.defaultMessage = defaultMessage;
    this.errorHandler = new ErrorHandler({ module: 'FallbackHandler', maxErrors: 10 });
  }

  /**
   * Display fallback message in container
   * @param {string} message - Optional override message
   */
  showFallback(message) {
    if (!isBrowser) return;
    try {
      const container = document.querySelector(this.containerSelector);
      if (!container) throw new Error(`Container not found: ${this.containerSelector}`);
      const msg = document.createElement('div');
      msg.className = 'fallback-message';
      msg.textContent = message || this.defaultMessage;
      container.innerHTML = '';
      container.appendChild(msg);
      this.errorHandler.logSuccess('Displayed fallback', { container: this.containerSelector });
    } catch (err) {
      this.errorHandler.handleError('showFallback failed', err, { container: this.containerSelector });
    }
  }

  /**
   * Execute fallback callback
   * @param {Function} callback
   */
  execute(callback) {
    try {
      if (typeof callback === 'function') callback();
      else throw new Error('Callback is not a function');
      this.errorHandler.logSuccess('Executed fallback callback');
    } catch (err) {
      this.errorHandler.handleError('execute failed', err);
    }
  }

  /**
   * Clear any existing fallback UI
   */
  clear() {
    if (!isBrowser) return;
    try {
      const container = document.querySelector(this.containerSelector);
      if (container) container.innerHTML = '';
      this.errorHandler.logSuccess('Cleared fallback UI');
    } catch (err) {
      this.errorHandler.handleError('clear failed', err);
    }
  }
}

// Named export for direct use
export { FallbackHandler };

// Default export with defaults
export default new FallbackHandler();
